import React, { useState, useEffect } from 'react';
import { Plus, Minus, Trash2, X, ArrowUp, ArrowDown, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Badge } from '../ui/badge';
import { getEventsForMeet, getGenderLabel } from '../../utils/eventConfig';
import { ALL_EVENTS } from '../../utils/settings';
import {
  getDefaultEventsForSport,
  getSportVocab,
  shouldShowFieldEvents,
  normalizeSportType,
  CUSTOM_EVENT_DEFAULT_DURATION_SECONDS,
  CUSTOM_EVENT_DEFAULT_HEATS,
  secondsToMMSS,
  SWIM_YOUTH_BASELINES,
  SWIM_SENIOR_BASELINES,
} from '../../utils/sportConfig';
import { getSettings } from '../../utils/settings';
import Header from '../Header';
import BulkEventBuilder from './BulkEventBuilder';

// ─── Summer-Club UI helpers (Feb 2026) ─────────────────────────────
// Used ONLY when sportType==='swim' && swim_preset==='summer_club'.
// All other sport/preset combos bypass these entirely.

// Canonical render order for the stroke-folder accordion. Order
// matches the conventional flow of a USA-Swimming age-group meet:
// medley relays open, individual strokes in the middle, freestyle
// relays close. "Other" catches anything our detector can't classify
// so no event is ever silently hidden.
const SUMMER_CLUB_STROKE_CATEGORIES = [
  'Medley Relays',
  'Freestyle',
  'Backstroke',
  'Breaststroke',
  'Butterfly',
  'IM',
  'Freestyle Relays',
  'Other',
];

// Order-sensitive: relay variants MUST match before the bare stroke
// regex, otherwise "Freestyle Relay" would route to "Freestyle".
function getStrokeCategory(eventName) {
  if (!eventName) return 'Other';
  if (/Medley Relay/i.test(eventName)) return 'Medley Relays';
  if (/Freestyle Relay/i.test(eventName)) return 'Freestyle Relays';
  if (/Backstroke/i.test(eventName)) return 'Backstroke';
  if (/Breaststroke/i.test(eventName)) return 'Breaststroke';
  if (/Butterfly/i.test(eventName)) return 'Butterfly';
  if (/\bIM\b/i.test(eventName)) return 'IM';
  if (/Freestyle/i.test(eventName)) return 'Freestyle';
  return 'Other';
}

// Templates seeded by the inline "+ Add {Stroke} Event" button. Each
// matches the same row shape the preset emits so the schedule engine
// + backend MeetEvent model see no difference. Durations align with
// SWIM_YOUTH_* baselines (50 sprint = 105s/4 heats; 100 = 180s/3;
// relay = 210s/2). Coach can edit any field after the row drops in.
const STROKE_DEFAULTS = {
  'Medley Relays':    { distance: 100, stroke: 'Medley Relay',    durationSec: 210, heats: 2, type: 'relay' },
  'Freestyle Relays': { distance: 100, stroke: 'Freestyle Relay', durationSec: 210, heats: 2, type: 'relay' },
  'Freestyle':        { distance: 50,  stroke: 'Freestyle',       durationSec: 105, heats: 4, type: 'sprint' },
  'Backstroke':       { distance: 50,  stroke: 'Backstroke',      durationSec: 105, heats: 4, type: 'sprint' },
  'Breaststroke':     { distance: 50,  stroke: 'Breaststroke',    durationSec: 105, heats: 4, type: 'sprint' },
  'Butterfly':        { distance: 50,  stroke: 'Butterfly',       durationSec: 105, heats: 4, type: 'sprint' },
  'IM':               { distance: 100, stroke: 'IM',              durationSec: 180, heats: 3, type: 'distance' },
  'Other':            { distance: 50,  stroke: 'Freestyle',       durationSec: 105, heats: 4, type: 'sprint' },
};

// Zero-typing age picker catalog. "6 & Under" + "Open" act as
// single-cell selections (min === max). Numeric ages 7..18 form a
// range when min !== max.
const SUMMER_CLUB_AGE_OPTIONS = [
  '6 & Under', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', 'Open',
];

// Parse a stored age string back into {min, max} so the dropdowns
// reflect the saved state when an existing event row is re-opened.
// Unknown free-form tags (e.g. "Masters", "11-14") that don't fit
// the catalog return blank — the coach picks fresh values.
function parseAgeRange(raw) {
  if (!raw) return { min: '', max: '' };
  const s = String(raw).trim();
  if (s === '6 & Under' || s === 'Open') return { min: s, max: s };
  const m = s.match(/^(\d+)\s*-\s*(\d+)$/);
  if (m) {
    const lo = m[1];
    const hi = m[2];
    if (SUMMER_CLUB_AGE_OPTIONS.includes(lo) && SUMMER_CLUB_AGE_OPTIONS.includes(hi)) {
      return { min: lo, max: hi };
    }
  }
  if (/^\d+$/.test(s) && SUMMER_CLUB_AGE_OPTIONS.includes(s)) return { min: s, max: s };
  return { min: '', max: '' };
}

// Concatenate {min, max} back into the canonical string the rest of
// the app expects on `event.ageGroup`. Mirrors how a coach would
// hand-type it: "9-10", "11-12", "6 & Under", "Open".
function formatAgeRange(min, max) {
  if (!min && !max) return null;
  if (!min) return max || null;
  if (!max) return min || null;
  if (min === max) return min;
  // "6 & Under" / "Open" act as a single bracket regardless of the
  // other side — collapse to the special label so the saved string
  // matches what a human would write.
  if (min === '6 & Under') return '6 & Under';
  if (max === '6 & Under') return '6 & Under';
  if (min === 'Open') return 'Open';
  if (max === 'Open') return 'Open';
  return `${min}-${max}`;
}

// Derive the stroke-folder render order from the current events
// array (Summer-Club only). Categories with events appear in the
// order their first event occurs in the flat array — this lets the
// stroke-block reordering action (move-up / move-down on accordion
// header) persist across save / re-open by mutating the events
// array itself, not a separate order field. Empty categories are
// appended in the canonical order so coaches can still tap their
// inline + Add button.
function deriveStrokeOrder(events) {
  const seen = new Set();
  const fromEvents = [];
  (events || []).forEach((e) => {
    const c = getStrokeCategory(e.name);
    if (!seen.has(c)) {
      seen.add(c);
      fromEvents.push(c);
    }
  });
  const result = [...fromEvents];
  SUMMER_CLUB_STROKE_CATEGORIES.forEach((c) => {
    if (!seen.has(c)) result.push(c);
  });
  return result;
}

// Zero-typing distance picker catalog. Covers short-course sprints
// through long-distance freestyle (1650y = NFHS 1500m mile). Used
// only when a Summer-Club EventCard exposes its Distance dropdown.
const SUMMER_CLUB_DISTANCE_OPTIONS = [25, 50, 100, 200, 400, 500, 800, 1000, 1500, 1650];

// Parse a swim-event name (e.g. "50y Freestyle", "100m Medley Relay")
// back into { distance, unit, stroke } so the Distance dropdown can
// initialise to the right value and rebuild the name when the coach
// picks a new distance. If the name doesn't fit the canonical shape
// (legacy free-form like "Custom Race"), we return sensible defaults
// and let the dropdown re-stamp a clean name on next change.
function parseSwimEventName(name) {
  if (!name) return { distance: 50, unit: 'y', stroke: 'Freestyle' };
  const m = String(name).match(/^\s*(\d+)\s*([ym])?\s+(.+?)\s*$/);
  if (m) {
    return {
      distance: parseInt(m[1], 10),
      unit: (m[2] === 'm' || m[2] === 'y') ? m[2] : 'y',
      stroke: m[3],
    };
  }
  return { distance: 50, unit: 'y', stroke: String(name) };
}

// Concatenate distance + unit + stroke back into the canonical
// "50y Freestyle" shape. Used by the Distance dropdown handler.
function formatSwimEventName(distance, unit, stroke) {
  const u = (unit === 'm') ? 'm' : 'y';
  return `${distance}${u} ${stroke}`;
}

// Pre-index Swim baselines by id so the Summer-Club baseline lookup
// is O(1). Same shape as BulkEventBuilder.jsx — we keep a private
// copy here to avoid an awkward circular import.
const SUMMER_CLUB_BASELINE_INDEX = (() => {
  const out = {};
  [...SWIM_SENIOR_BASELINES, ...SWIM_YOUTH_BASELINES].forEach((row) => {
    out[row.id] = row;
  });
  return out;
})();

// Map (distance, isRelay) → SWIM_YOUTH_*-first baseline id. Summer
// Club is always youth, so we prefer the youth catalog for 25/50/
// 100 and the youth relay; fall back to the senior baselines for
// longer races where no youth row exists (200, 400/500 distance).
function pickSummerClubBaselineKey(distance, isRelay) {
  if (isRelay) return 'swim_youth_relay';
  if (distance <= 25) return 'swim_youth_25_sprint';
  if (distance <= 50) return 'swim_youth_50_sprint';
  if (distance <= 100) return 'swim_youth_100';
  if (distance <= 200) return 'swim_senior_200';
  return 'swim_senior_500_distance';
}

// Pull the live default duration + heats for a (distance, stroke)
// pair, respecting the user's localStorage overrides via
// `getSettings()`. For ultra-long events (>500) we scale the
// 500-distance baseline proportionally so a 1650y mile doesn't
// share the 500's 7:30 default. Relays are returned at their
// catalog value (never scaled — relays are fixed-distance events).
function getSummerClubBaseline(distance, stroke, settings) {
  const isRelay = /Relay/i.test(String(stroke || ''));
  const key = pickSummerClubBaselineKey(distance, isRelay);
  const row = SUMMER_CLUB_BASELINE_INDEX[key];
  const fallbackDuration = row?.default_duration_seconds ?? 90;
  const fallbackHeats = row?.default_heats ?? 2;
  const s = settings || {};
  let duration = s[`${key}_duration`] ?? fallbackDuration;
  const heats = s[`${key}_heats`] ?? fallbackHeats;
  if (!isRelay && distance > 500) {
    const ratio = distance / 500;
    duration = Math.round(duration * ratio);
  }
  return { default_duration_seconds: duration, default_heats: heats };
}

// XC smart defaults (Feb 2026). Schedule must be based on the time
// the SLOWEST runner needs to finish + buffer to clear the course.
// Returns the default duration (seconds) for a given division +
// gender combo. Gender is one of 'boys' | 'girls' | 'mixed'.
// Combined / Mixed shares the Girls value (the slower-of-the-two
// gender baselines becomes the safe scheduling window for an open
// race).
function getXcDefaultDurationSeconds(ageGroup, gender) {
  const ag = String(ageGroup || '').toLowerCase();
  const isVarsity = /varsity/.test(ag);
  // JV / Freshman / Sophomore developmental races share the same
  // longer slow-runner window.
  const isJVFreshman = /\bjv\b|junior\s*varsity|freshman|frosh|soph/.test(ag);
  // Middle School / Junior High catches the typical youth labels.
  const isMS = /middle|junior\s*high|\bms\b|7th|8th|6th/.test(ag);
  const isBoys = gender === 'boys';
  if (isVarsity) {
    return isBoys ? 30 * 60 : 35 * 60;   // 30:00 vs 35:00
  }
  if (isJVFreshman) {
    return isBoys ? 40 * 60 : 45 * 60;   // 40:00 vs 45:00
  }
  if (isMS) {
    return isBoys ? 25 * 60 : 30 * 60;   // 25:00 vs 30:00
  }
  return isBoys ? 35 * 60 : 40 * 60;     // fallback 35:00 / 40:00
}

function EventCard({ event, onUpdate, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, genderLabels, hideGenderToggle, singleGenderMode, useAgeDropdowns, useDistanceDropdown, poolUnit, defaultExpanded, xcDurationRecompute }) {
  const [isExpanded, setIsExpanded] = useState(Boolean(defaultExpanded));

  // Min/Max age dropdown state — Summer-Club only. Held LOCALLY and
  // independently so picking Min=13 does NOT clobber Max (or vice
  // versa). We initialise from the saved `event.ageGroup` string at
  // mount, then own the state for the lifetime of this tile. The
  // parent's `event.ageGroup` is only updated via formatAgeRange()
  // inside the onValueChange handlers below, so the local copy
  // stays the source of truth for the dropdowns.
  const initialAge = parseAgeRange(event.ageGroup);
  const [minAge, setMinAge] = useState(initialAge.min);
  const [maxAge, setMaxAge] = useState(initialAge.max);

  const getDurationOptions = (baseDuration) => {
    // Generate durations from 1:00 to 25:00 in 30-second increments
    const durations = [];
    for (let min = 1; min <= 25; min++) {
      durations.push(`${min.toString().padStart(2, '0')}:00`);
      if (min < 25) {
        durations.push(`${min.toString().padStart(2, '0')}:30`);
      }
    }
    // Add the base duration if it's not already in the list (preserves existing event defaults)
    if (baseDuration && !durations.includes(baseDuration)) {
      durations.push(baseDuration);
      durations.sort();
    }
    return durations;
  };
  
  const getSummaryInfo = () => {
    const parts = [];
    if (event.showGirls) {
      parts.push(`${genderLabels.female}: ${event.girlsHeats || 1}H`);
    }
    if (event.showBoys) {
      parts.push(`${genderLabels.male}: ${event.boysHeats || 1}H`);
    }
    if (event.combined) {
      parts.push(`Mixed: ${event.girlsHeats || 1}H`);
    }
    return parts.join(' • ');
  };

  if (!event.enabled) {
    return (
      <Card className="p-4 bg-muted/50 border-dashed">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Checkbox 
              checked={false}
              onCheckedChange={() => onUpdate({ ...event, enabled: true })}
            />
            <span className="text-muted-foreground">{event.name}</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 hover:shadow-elegant transition-smooth">
      <div className="space-y-3">
        {/* Header - Always visible */}
        <div className="flex items-start gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Checkbox 
              checked={event.enabled}
              onCheckedChange={() => onUpdate({ ...event, enabled: false })}
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </Button>
          </div>

          <div 
            className="flex-1 cursor-pointer min-w-0"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{event.name}</h3>
              {event.ageGroup && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-purple-50 text-purple-700 border-purple-200">
                  {event.ageGroup}
                </Badge>
              )}
            </div>
            {!isExpanded && (
              <p className="text-xs text-muted-foreground mt-1">{getSummaryInfo()}</p>
            )}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              title="Move up"
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              title="Move down"
            >
              <ArrowDown className="w-4 h-4" />
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onRemove(event.id)}
              title="Remove event"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <>
            {/* Gender Sections */}
            {(() => {
              const sections = [];
              
              if (event.showGirls) {
                sections.push({
                  type: 'girls',
                  order: event.girlsOrder || 0,
                  component: (
                    <div key="girls" className="space-y-2 p-3 rounded-lg gender-girls border">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">{genderLabels.female}</h4>
                        {event.showBoys && (
                          <div className="flex items-center gap-1">
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6"
                              onClick={() => onUpdate({ ...event, girlsOrder: (event.girlsOrder || 0) - 1 })}
                              title="Move girls section up"
                            >
                              <ArrowUp className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6"
                              onClick={() => onUpdate({ ...event, girlsOrder: (event.girlsOrder || 0) + 1 })}
                              title="Move girls section down"
                            >
                              <ArrowDown className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Heats</Label>
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => onUpdate({ ...event, girlsHeats: Math.max(1, (event.girlsHeats || 1) - 1) })}
                              title="Decrease heats"
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <Input 
                              type="number"
                              min="1"
                              value={event.girlsHeats || 1}
                              onChange={(e) => onUpdate({ ...event, girlsHeats: parseInt(e.target.value) || 1 })}
                              className="h-8 text-center"
                            />
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => onUpdate({ ...event, girlsHeats: (event.girlsHeats || 1) + 1 })}
                              title="Increase heats"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Duration</Label>
                          <Select 
                            value={event.girlsDuration}
                            onValueChange={(val) => onUpdate({ ...event, girlsDuration: val })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getDurationOptions(event.girlsDuration).map(dur => (
                                <SelectItem key={dur} value={dur}>
                                  {dur === event.defaultGirlsDuration ? `${dur} (default)` : dur}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )
                });
              }
              
              if (event.showBoys) {
                sections.push({
                  type: 'boys',
                  order: event.boysOrder || 0,
                  component: (
                    <div key="boys" className="space-y-2 p-3 rounded-lg gender-boys border">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">{genderLabels.male}</h4>
                        {event.showGirls && (
                          <div className="flex items-center gap-1">
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6"
                              onClick={() => onUpdate({ ...event, boysOrder: (event.boysOrder || 0) - 1 })}
                              title="Move boys section up"
                            >
                              <ArrowUp className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6"
                              onClick={() => onUpdate({ ...event, boysOrder: (event.boysOrder || 0) + 1 })}
                              title="Move boys section down"
                            >
                              <ArrowDown className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Heats</Label>
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => onUpdate({ ...event, boysHeats: Math.max(1, (event.boysHeats || 1) - 1) })}
                              title="Decrease heats"
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <Input 
                              type="number"
                              min="1"
                              value={event.boysHeats || 1}
                              onChange={(e) => onUpdate({ ...event, boysHeats: parseInt(e.target.value) || 1 })}
                              className="h-8 text-center"
                            />
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => onUpdate({ ...event, boysHeats: (event.boysHeats || 1) + 1 })}
                              title="Increase heats"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Duration</Label>
                          <Select 
                            value={event.boysDuration}
                            onValueChange={(val) => onUpdate({ ...event, boysDuration: val })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getDurationOptions(event.boysDuration).map(dur => (
                                <SelectItem key={dur} value={dur}>
                                  {dur === event.defaultBoysDuration ? `${dur} (default)` : dur}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )
                });
              }
              
              if (event.combined) {
                sections.push({
                  type: 'combined',
                  order: 0,
                  component: (
                    <div key="combined" className="space-y-2 p-3 rounded-lg bg-muted border">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Mixed (Boys &amp; Girls)</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Heats</Label>
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => onUpdate({ ...event, girlsHeats: Math.max(1, (event.girlsHeats || 1) - 1), boysHeats: Math.max(1, (event.girlsHeats || 1) - 1) })}
                              title="Decrease heats"
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <Input 
                              type="number"
                              min="1"
                              value={event.girlsHeats || 1}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                onUpdate({ ...event, girlsHeats: val, boysHeats: val });
                              }}
                              className="h-8 text-center"
                            />
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => {
                                const newVal = (event.girlsHeats || 1) + 1;
                                onUpdate({ ...event, girlsHeats: newVal, boysHeats: newVal });
                              }}
                              title="Increase heats"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Duration</Label>
                          <Select 
                            value={event.girlsDuration}
                            onValueChange={(val) => onUpdate({ ...event, girlsDuration: val, boysDuration: val })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getDurationOptions(event.girlsDuration).map(dur => (
                                <SelectItem key={dur} value={dur}>
                                  {dur === event.defaultGirlsDuration ? `${dur} (default)` : dur}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )
                });
              }

              return sections.sort((a, b) => a.order - b.order).map(s => s.component);
            })()}

            {/* Distance picker — Summer-Club zero-typing path. Lets
                the coach swap the distance on an event without
                opening a keyboard or having to delete/re-add. The
                event's `name` is re-stamped from the canonical
                `${distance}${unit} ${stroke}` shape; unit is taken
                from the parsed existing name (falling back to the
                meet-level pool_unit prop), so picking 25 → 100
                doesn't accidentally swap yards ↔ meters. */}
            {useDistanceDropdown && (() => {
              const parsed = parseSwimEventName(event.name);
              // Prefer the unit baked into the event name; otherwise
              // fall back to the meet's pool unit (defaults to yards).
              const resolvedUnit = parsed.unit
                || (poolUnit === 'meters' ? 'm' : 'y');
              return (
                <div className="space-y-1 pt-2 border-t border-border" data-testid={`event-distance-row-${event.id}`}>
                  <Label className="text-xs">Distance</Label>
                  <Select
                    value={String(parsed.distance)}
                    onValueChange={(v) => {
                      const dist = parseInt(v, 10);
                      // Recompute the SWIM_YOUTH_* baseline for the
                      // new (distance, stroke) pair so the schedule
                      // predictor stays mathematically accurate. Pull
                      // live settings via getSettings() so any user
                      // customisations on the Settings page are
                      // honoured. Heats + durations are stamped onto
                      // both the live value AND the default value
                      // so the "untouched" detector on subsequent
                      // gender toggles can still spot a fresh row.
                      const newName = formatSwimEventName(dist, resolvedUnit, parsed.stroke);
                      const baseline = getSummerClubBaseline(dist, parsed.stroke, getSettings());
                      const newDuration = secondsToMMSS(baseline.default_duration_seconds);
                      const newHeats = baseline.default_heats;
                      onUpdate({
                        ...event,
                        name: newName,
                        girlsDuration: newDuration,
                        boysDuration: newDuration,
                        defaultGirlsDuration: newDuration,
                        defaultBoysDuration: newDuration,
                        girlsHeats: newHeats,
                        boysHeats: newHeats,
                      });
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm" data-testid={`event-distance-${event.id}`}>
                      <SelectValue placeholder="Distance" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUMMER_CLUB_DISTANCE_OPTIONS.map((d) => (
                        <SelectItem
                          key={d}
                          value={String(d)}
                          data-testid={`event-distance-opt-${event.id}-${d}`}
                        >
                          {d}{resolvedUnit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}

            {/* Age Group / Division. Summer-Club mode (useAgeDropdowns
                === true) replaces the free-text input with two
                native dropdowns [Min Age] [Max Age] — zero keyboard
                interaction. Track / XC / Standard-HS Swim keep the
                free-text input for the irregular brackets coaches
                use there ("11-14", "Varsity", "Masters", etc.). The
                stored `event.ageGroup` string format is identical in
                both modes, so the schedule engine + backend round-
                trip is unaffected. */}
            {useAgeDropdowns ? (
              <div className="space-y-1 pt-2 border-t border-border" data-testid={`event-age-dropdowns-${event.id}`}>
                <Label className="text-xs">Age Group</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={minAge || undefined}
                    onValueChange={(v) => {
                      // Update ONLY the min local state. The other
                      // dropdown's state is untouched — picking Min
                      // never auto-fills Max (and vice versa). The
                      // saved `event.ageGroup` is recomputed from
                      // both local values; canonicalization
                      // (6 & Under / Open collapse, blank handling)
                      // lives entirely inside formatAgeRange.
                      setMinAge(v);
                      onUpdate({ ...event, ageGroup: formatAgeRange(v, maxAge) });
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm flex-1" data-testid={`event-age-min-${event.id}`}>
                      <SelectValue placeholder="Min" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUMMER_CLUB_AGE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} data-testid={`event-age-min-opt-${event.id}-${opt}`}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span
                    className="text-xs text-muted-foreground shrink-0"
                    data-testid={`event-age-to-label-${event.id}`}
                  >
                    to
                  </span>
                  <Select
                    value={maxAge || undefined}
                    onValueChange={(v) => {
                      setMaxAge(v);
                      onUpdate({ ...event, ageGroup: formatAgeRange(minAge, v) });
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm flex-1" data-testid={`event-age-max-${event.id}`}>
                      <SelectValue placeholder="Max" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUMMER_CLUB_AGE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} data-testid={`event-age-max-opt-${event.id}-${opt}`}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="space-y-1 pt-2 border-t border-border">
                <Label className="text-xs">Age Group / Division (optional)</Label>
                <Input
                  type="text"
                  placeholder="e.g. 11-12, 6 & Under, Varsity, Open"
                  value={event.ageGroup || ''}
                  onChange={(e) => {
                    const newAg = e.target.value || null;
                    let updated = { ...event, ageGroup: newAg };
                    // XC smart-default refresh on division re-label.
                    // GUARDRAIL: only overwrite a per-gender duration
                    // when it still matches its `defaultDuration`
                    // sibling (untouched / pristine). Custom edits
                    // the coach already made stay intact. The default
                    // tracker is ALSO only updated alongside the live
                    // value so a stale default never marks a custom
                    // value as untouched on a future re-label.
                    if (xcDurationRecompute && newAg !== event.ageGroup) {
                      const newGirlsStr = secondsToMMSS(getXcDefaultDurationSeconds(newAg, 'girls'));
                      const newBoysStr = secondsToMMSS(getXcDefaultDurationSeconds(newAg, 'boys'));
                      if (event.girlsDuration === event.defaultGirlsDuration) {
                        updated.girlsDuration = newGirlsStr;
                        updated.defaultGirlsDuration = newGirlsStr;
                      }
                      if (event.boysDuration === event.defaultBoysDuration) {
                        updated.boysDuration = newBoysStr;
                        updated.defaultBoysDuration = newBoysStr;
                      }
                    }
                    onUpdate(updated);
                  }}
                  className="h-8 text-sm"
                  data-testid={`event-age-group-${event.id}`}
                />
              </div>
            )}

            {/* Gender Selection. Swim meets use mutually-exclusive
                radio semantics (singleGenderMode=true) — picking
                Girls clears Boys + Mixed, picking Boys clears Girls
                + Mixed, picking Mixed clears Girls + Boys. The Swim
                event tile thus only ever renders ONE of the
                Girls/Boys/Mixed configuration rows above. Track &
                XC keep the legacy multi-checkbox behavior so Girls
                + Boys can stack in a single tile. `hideGenderToggle`
                stays available for callers that want to suppress
                the toggle entirely (no current users — defaults to
                false). */}
            {!hideGenderToggle && (
              <div className="flex gap-3 pt-2 border-t border-border">
                <Checkbox
                  id={`girls-${event.id}`}
                  checked={event.showGirls}
                  onCheckedChange={(checked) => {
                    if (singleGenderMode) {
                      // Radio behavior: a positive tap commits to
                      // Girls-only; tapping an already-checked
                      // option is a no-op (radios never deselect
                      // themselves — coach must pick another).
                      if (checked) {
                        onUpdate({ ...event, showGirls: true, showBoys: false, combined: false });
                      }
                    } else {
                      onUpdate({ ...event, showGirls: checked, combined: checked ? false : event.combined });
                    }
                  }}
                  data-testid={`event-gender-girls-${event.id}`}
                />
                <Label htmlFor={`girls-${event.id}`} className="text-sm cursor-pointer">
                  {genderLabels.female}
                </Label>

                <Checkbox
                  id={`boys-${event.id}`}
                  checked={event.showBoys}
                  onCheckedChange={(checked) => {
                    if (singleGenderMode) {
                      if (checked) {
                        onUpdate({ ...event, showGirls: false, showBoys: true, combined: false });
                      }
                    } else {
                      onUpdate({ ...event, showBoys: checked, combined: checked ? false : event.combined });
                    }
                  }}
                  data-testid={`event-gender-boys-${event.id}`}
                />
                <Label htmlFor={`boys-${event.id}`} className="text-sm cursor-pointer">
                  {genderLabels.male}
                </Label>

                <Checkbox
                  id={`combined-${event.id}`}
                  checked={event.combined}
                  onCheckedChange={(checked) => {
                    if (singleGenderMode) {
                      if (checked) {
                        onUpdate({ ...event, showGirls: false, showBoys: false, combined: true });
                      }
                    } else {
                      onUpdate({ ...event, combined: checked, showGirls: false, showBoys: false });
                    }
                  }}
                  data-testid={`event-gender-mixed-${event.id}`}
                />
                <Label htmlFor={`combined-${event.id}`} className="text-sm cursor-pointer">
                  Mixed
                </Label>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

export default function ManualEventEntry({ data, onUpdate, onNext, onBack, isEditing = false }) {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  // Quick-add inputs for the "Add Custom Event" card. Kept local — they
  // don't need to round-trip to the wizard's outer form, and clearing
  // after add gives the coach a fresh row to keep typing.
  const [customEventName, setCustomEventName] = useState('');
  const [customEventAgeGroup, setCustomEventAgeGroup] = useState('');
  const genderLabels = getGenderLabel(data.level);
  const sportType = normalizeSportType(data.sport_type);
  const vocab = getSportVocab(sportType);
  const showFieldEvents = shouldShowFieldEvents(sportType);

  // Summer-Club mode unlocks the zero-typing UI overhaul: collapsible
  // stroke folders + inline per-stroke Add button + Min/Max age
  // dropdowns. Strictly gated on (swim + summer_club); HS swim,
  // Track, and XC keep the existing flat list + free-text age input.
  const isSummerClub = sportType === 'swim' && data.swim_preset === 'summer_club';

  // IDs of events that should mount with the tile already expanded.
  // Populated by the inline "+ Add {Stroke} Event" button so the
  // coach skips a second tap. A Set is fine — we only consult it on
  // initial EventCard mount (defaultExpanded prop).
  const [autoExpandedIds, setAutoExpandedIds] = useState(() => new Set());

  // Summer-Club stroke-folder render order. Default = canonical
  // (Medley Relays → Freestyle → … → Other), but the user can
  // reorder via the Up/Down arrows on each AccordionTrigger and the
  // change persists by reordering the flat events array. On load
  // (data.events present), we derive from event-array order so the
  // saved order survives across sessions.
  const [strokeOrder, setStrokeOrder] = useState(SUMMER_CLUB_STROKE_CATEGORIES);

  const editMode = isEditing || (data.id !== undefined);

  useEffect(() => {
    if (data.events && data.events.length > 0) {
      setEvents(data.events);
      // Sync the Summer-Club stroke folder order to whatever the
      // saved events array dictates so the rendered accordion order
      // matches the persisted order after re-opening a meet.
      setStrokeOrder(deriveStrokeOrder(data.events));
      return;
    }

    // Sport-specific event template selection.
    //   - track: keep the existing level/type-keyed config in eventConfig.js
    //   - swim/xc: pull from the flat per-sport defaults in sportConfig.js,
    //     forwarding the chosen swim preset (standard_hs vs summer_club)
    //     so club coaches see age-group events seeded.
    let eventTemplates;
    if (sportType === 'track') {
      eventTemplates = getEventsForMeet(data.level, data.type);
    } else {
      eventTemplates = getDefaultEventsForSport(
        sportType,
        data.level,
        { swimPreset: data.swim_preset, poolUnit: data.pool_unit },
      ) || [];
    }

    // Defensive: when the sport is swim or xc we strip any field-typed
    // event the template happened to include. (Currently a no-op since
    // ALL_EVENTS has no field type, but the wizard must be future-proof
    // against settings changes.)
    if (!showFieldEvents) {
      eventTemplates = eventTemplates.filter((t) => t.type !== 'field');
    }

    const isSwim = sportType === 'swim';
    // Summer-Club EventCards use stacked-checkbox semantics (like
    // Track & Field), so seed BOTH Girls + Boys on by default — the
    // coach can disable either side per-tile. Standard-HS Swim
    // keeps the single-gender default since its tiles are
    // mutually exclusive (Feb 2026 user decision).
    const isSummerClubSeed = isSwim && data.swim_preset === 'summer_club';
    const initialEvents = eventTemplates.map((template, idx) => {
      // The template already has defaultHeats and duration from level-specific settings
      const heats = template.defaultHeats || 3;
      const duration = template.duration || '03:00';

      const swimDefaultGirls = !template.boysOnly && !template.menOnly;
      const showGirls = !template.boysOnly && !template.menOnly;
      const showBoys = isSummerClubSeed
        ? (!template.girlsOnly && !template.womenOnly)
        : isSwim
          ? (!swimDefaultGirls && !template.girlsOnly && !template.womenOnly)
          : (!template.girlsOnly && !template.womenOnly);

      // XC events get gender-specific smart defaults so the
      // schedule predictor mirrors how slow runners actually finish
      // (Feb 2026 user spec). Girls / Mixed share the slower bucket;
      // Boys gets the shorter race window per division. Track + Swim
      // keep the level-baseline `duration` passed in by the preset.
      const isXc = sportType === 'xc';
      const xcGirlsDur = isXc
        ? secondsToMMSS(getXcDefaultDurationSeconds(template.ageGroup, 'girls'))
        : duration;
      const xcBoysDur = isXc
        ? secondsToMMSS(getXcDefaultDurationSeconds(template.ageGroup, 'boys'))
        : duration;

      return {
        id: `event-${idx}`,
        name: template.name,
        type: template.type,
        eventId: template.eventId,
        enabled: true,
        showGirls,
        showBoys,
        combined: false,
        // ageGroup carries through from swim/xc presets that seed it
        // (e.g. summer-club "11-12"). Track templates leave it null.
        ageGroup: template.ageGroup || null,
        girlsHeats: heats,
        boysHeats: heats,
        girlsDuration: xcGirlsDur,
        boysDuration: xcBoysDur,
        defaultGirlsDuration: xcGirlsDur,
        defaultBoysDuration: xcBoysDur,
        girlsOrder: 0,
        boysOrder: 0,
      };
    });

    setEvents(initialEvents);
  }, [data.level, data.type, data.events, data.swim_preset, data.pool_unit, sportType, showFieldEvents]);

  const updateEvent = (updatedEvent) => {
    setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };

  const removeEvent = (eventId) => {
    setEvents(prev => prev.filter(e => e.id !== eventId));
  };

  const moveEvent = (index, direction) => {
    const newEvents = [...events];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newEvents.length) return;
    
    [newEvents[index], newEvents[targetIndex]] = [newEvents[targetIndex], newEvents[index]];
    setEvents(newEvents);
  };

  const addPredefinedEvent = () => {
    if (!selectedEventId) return;

    let selectedEvent;
    let heats;
    let duration;
    let ageGroup = null;

    if (sportType === 'track') {
      selectedEvent = ALL_EVENTS.find((e) => e.id === selectedEventId);
      if (!selectedEvent) return;
      const normalizedLevel = data.level.replace('-school', '').replace('professional', 'college');
      const levelDefaults = selectedEvent.levels?.[normalizedLevel];
      heats = levelDefaults?.heats || 3;
      duration = levelDefaults?.duration || '03:00';
    } else {
      // Swim/XC pickers source their list from the sport defaults rather
      // than ALL_EVENTS, so resolve by eventId there.
      const sportList = getDefaultEventsForSport(
        sportType,
        data.level,
        { swimPreset: data.swim_preset, poolUnit: data.pool_unit },
      ) || [];
      selectedEvent = sportList.find((e) => e.eventId === selectedEventId);
      if (!selectedEvent) return;
      heats = selectedEvent.defaultHeats || 1;
      duration = selectedEvent.duration || '03:00';
      ageGroup = selectedEvent.ageGroup || null;
    }

    const isSwimAdd = sportType === 'swim';
    const isSummerClubAdd = isSwimAdd && data.swim_preset === 'summer_club';
    const isXcAdd = sportType === 'xc';
    // XC: gender-specific defaults from the smart lookup. Track +
    // Swim: keep the level-baseline duration the picker returned.
    const xcGirlsDur = isXcAdd
      ? secondsToMMSS(getXcDefaultDurationSeconds(ageGroup, 'girls'))
      : duration;
    const xcBoysDur = isXcAdd
      ? secondsToMMSS(getXcDefaultDurationSeconds(ageGroup, 'boys'))
      : duration;
    const newEvent = {
      id: `added-${Date.now()}`,
      name: selectedEvent.name,
      type: selectedEvent.type,
      eventId: selectedEvent.eventId || selectedEvent.id,
      enabled: true,
      // Summer-Club + Track / XC seed BOTH genders (stacked
      // checkboxes). Standard-HS Swim keeps the single-gender
      // default since its tiles are mutually exclusive.
      showGirls: true,
      showBoys: isSummerClubAdd ? true : !isSwimAdd,
      combined: false,
      ageGroup,
      girlsHeats: heats,
      boysHeats: heats,
      girlsDuration: xcGirlsDur,
      boysDuration: xcBoysDur,
      defaultGirlsDuration: xcGirlsDur,
      defaultBoysDuration: xcBoysDur,
      girlsOrder: 0,
      boysOrder: 0,
    };

    setEvents((prev) => [...prev, newEvent]);
    setSelectedEventId('');
  };

  // Bulk-add quick path for summer-club coaches: tap "Add Custom Event"
  // → enter a name + age group → event lands with the sport's custom
  // duration default (60s) so the long tail of youth sprint events can
  // be added in seconds. Used when the canned preset doesn't cover the
  // exact stroke/distance/age combo a coach needs.
  const addCustomEvent = () => {
    const name = customEventName.trim();
    if (!name) return;
    const duration = secondsToMMSS(CUSTOM_EVENT_DEFAULT_DURATION_SECONDS);
    const isSwimCustom = sportType === 'swim';
    const isXcCustom = sportType === 'xc';
    const ageGroupTrimmed = customEventAgeGroup.trim() || null;
    // XC custom events still need the smart per-gender duration
    // (slow runner + course-clear) — coaches shouldn't have to
    // recompute the default by hand just because the race name
    // wasn't in the preset.
    const girlsDur = isXcCustom
      ? secondsToMMSS(getXcDefaultDurationSeconds(ageGroupTrimmed, 'girls'))
      : duration;
    const boysDur = isXcCustom
      ? secondsToMMSS(getXcDefaultDurationSeconds(ageGroupTrimmed, 'boys'))
      : duration;
    setEvents((prev) => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        name,
        type: sportType === 'swim' ? 'sprint' : 'distance',
        eventId: `custom_${Date.now()}`,
        enabled: true,
        // Swim → single-gender default (Girls). Track / XC → both.
        showGirls: true,
        showBoys: !isSwimCustom,
        combined: false,
        ageGroup: ageGroupTrimmed,
        girlsHeats: CUSTOM_EVENT_DEFAULT_HEATS,
        boysHeats: CUSTOM_EVENT_DEFAULT_HEATS,
        girlsDuration: girlsDur,
        boysDuration: boysDur,
        defaultGirlsDuration: girlsDur,
        defaultBoysDuration: boysDur,
        girlsOrder: 0,
        boysOrder: 0,
      },
    ]);
    setCustomEventName('');
    setCustomEventAgeGroup('');
  };

  // Summer-Club inline quick-add. Tap "+ Add {Stroke} Event" inside
  // an accordion folder → a single new flat MeetEvent row drops in
  // at the end of the events array, pre-stamped with that stroke's
  // canonical defaults (distance, duration, heats) so the coach
  // doesn't have to type. The new tile is auto-expanded so the
  // coach can immediately edit age / gender / heats without a
  // second tap. Same row shape as the preset emits — schedule
  // engine + backend MeetEvent model see no difference.
  const addStrokeEvent = (category) => {
    const tpl = STROKE_DEFAULTS[category] || STROKE_DEFAULTS.Other;
    const unitSuffix = (data.pool_unit === 'meters') ? 'm' : 'y';
    const name = `${tpl.distance}${unitSuffix} ${tpl.stroke}`;
    const duration = secondsToMMSS(tpl.durationSec);
    const newId = `strokeadd-${Date.now()}`;
    setEvents((prev) => [
      ...prev,
      {
        id: newId,
        name,
        type: tpl.type,
        eventId: `strokeadd_${category}_${Date.now()}`.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
        enabled: true,
        // Summer-Club mode → both genders seeded (stacked
        // checkboxes, matching Track & Field). Coach can toggle
        // either side off per-tile.
        showGirls: true,
        showBoys: true,
        combined: false,
        ageGroup: null,
        girlsHeats: tpl.heats,
        boysHeats: tpl.heats,
        girlsDuration: duration,
        boysDuration: duration,
        defaultGirlsDuration: duration,
        defaultBoysDuration: duration,
        girlsOrder: 0,
        boysOrder: 0,
      },
    ]);
    // Mark for auto-expand so EventCard mounts open.
    setAutoExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(newId);
      return next;
    });
  };

  // Reorder a whole stroke block via the Up/Down arrows on the
  // AccordionTrigger. Updates BOTH `strokeOrder` (so the rendered
  // accordion order changes immediately) AND the underlying flat
  // events array (so the new stroke ordering persists across save
  // / re-open). Moving an empty folder only mutates `strokeOrder`.
  const moveStrokeBlock = (category, direction) => {
    let neighbor = null;
    setStrokeOrder((prevOrder) => {
      const idx = prevOrder.indexOf(category);
      if (idx === -1) return prevOrder;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prevOrder.length) return prevOrder;
      neighbor = prevOrder[targetIdx];
      const next = [...prevOrder];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      return next;
    });
    if (!neighbor) return;
    setEvents((prevEvents) => {
      // Group events by category, preserving in-group order.
      const groups = new Map();
      const presentCategories = [];
      const seen = new Set();
      prevEvents.forEach((e) => {
        const c = getStrokeCategory(e.name);
        if (!groups.has(c)) groups.set(c, []);
        groups.get(c).push(e);
        if (!seen.has(c)) {
          seen.add(c);
          presentCategories.push(c);
        }
      });
      const idxA = presentCategories.indexOf(category);
      const idxB = presentCategories.indexOf(neighbor);
      // Only mutate the events array when BOTH categories have
      // events. Empty-folder moves are handled entirely by
      // setStrokeOrder above (nothing to persist in events).
      if (idxA === -1 || idxB === -1) return prevEvents;
      [presentCategories[idxA], presentCategories[idxB]] = [presentCategories[idxB], presentCategories[idxA]];
      const newEvents = [];
      presentCategories.forEach((c) => {
        (groups.get(c) || []).forEach((e) => newEvents.push(e));
      });
      return newEvents;
    });
  };

  // Get list of available events to add — ALL events valid for the meet's
  // school level, including events already in the meet. Duplicates are
  // intentional (e.g. 110m Hurdles for both prelims and finals).
  //
  // For swim/xc meets, exclude any field-typed events. ALL_EVENTS is
  // track-centric so for those sports we surface the per-sport default
  // list as the "Add Event" picker source instead — that way a swim
  // coach can re-add events they removed without seeing track sprints.
  const getAvailableEvents = () => {
    if (sportType !== 'track') {
      const list = getDefaultEventsForSport(
        sportType,
        data.level,
        { swimPreset: data.swim_preset, poolUnit: data.pool_unit },
      ) || [];
      return list
        .filter((e) => e.type !== 'field')
        .map((e) => ({
          id: e.eventId,
          name: e.name,
          type: e.type,
          defaultHeats: e.defaultHeats,
          duration: e.duration,
          ageGroup: e.ageGroup,
        }));
    }
    const normalizedLevel = data.level.replace('-school', '').replace('professional', 'college');
    return ALL_EVENTS
      .filter((e) => e.levels?.[normalizedLevel] != null)
      .filter((e) => showFieldEvents || e.type !== 'field');
  };

  const handleContinue = () => {
    const activeEvents = events.filter(e => e.enabled);
    onUpdate({ events: activeEvents });
    if (typeof onNext === 'function') {
      onNext({ events: activeEvents });
    }
  };

  const enabledCount = events.filter(e => e.enabled).length;

  return (
    <div className="min-h-screen bg-background">
      <Header 
        showBack={true} 
        onBack={onBack} 
        title={editMode ? `Edit ${vocab.events.charAt(0).toUpperCase() + vocab.events.slice(1)}` : `Configure ${vocab.events.charAt(0).toUpperCase() + vocab.events.slice(1)}`} 
      />
      
      <main className="max-w-2xl mx-auto px-4 py-6 pb-32">
        <div className="mb-6">
          <p className="text-muted-foreground">
            {editMode 
              ? `Modify the ${vocab.events} for this meet. Tap an item to expand and edit details.`
              : `Select and configure the ${vocab.events} for your meet. Tap an item to expand and edit details.`}
          </p>
        </div>

        {/* Events List. Summer-Club mode renders a render-time
            accordion grouped by stroke category — the events array
            itself is still flat. All other sport / preset combos
            render the legacy flat list unchanged. */}
        {isSummerClub ? (
          <div className="space-y-3 mb-6" data-testid="summer-club-stroke-folders">
            <Accordion type="multiple" className="space-y-2">
              {strokeOrder.map((category, orderIdx) => {
                // Compute group membership at render time. We track
                // each event's flat-array index so moveUp/moveDown
                // still wire into the existing moveEvent(index, dir)
                // helper. Movement happens only between adjacent
                // members of the SAME stroke folder so visual order
                // matches array order within each accordion.
                const members = events
                  .map((e, flatIdx) => ({ e, flatIdx }))
                  .filter(({ e }) => getStrokeCategory(e.name) === category);
                // "Other" only renders when it has members — no point
                // exposing an empty Other bucket to coaches.
                if (members.length === 0 && category === 'Other') return null;
                const canMoveBlockUp = orderIdx > 0;
                // Only allow moving down past categories that will
                // actually render — i.e. skip an empty trailing
                // "Other" so the Down arrow doesn't aim at nothing.
                const lastRenderableIdx = (() => {
                  let last = strokeOrder.length - 1;
                  while (last >= 0) {
                    const c = strokeOrder[last];
                    if (c === 'Other') {
                      const hasOther = events.some((e) => getStrokeCategory(e.name) === 'Other');
                      if (!hasOther) { last -= 1; continue; }
                    }
                    break;
                  }
                  return last;
                })();
                const canMoveBlockDown = orderIdx < lastRenderableIdx;
                return (
                  <AccordionItem
                    key={category}
                    value={category}
                    className="border rounded-md bg-card"
                    data-testid={`stroke-folder-${category.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="flex items-center px-2">
                      {/* Up/Down arrows live OUTSIDE the trigger so a
                          tap doesn't toggle the accordion. 44×44px
                          touch targets (Apple HIG / Android-spec
                          minimum) and laid out side-by-side with a
                          visible gap so a tablet coach can't
                          accidentally tap the wrong arrow when the
                          opposite one is the one they want. */}
                      <div className="flex items-center gap-2 pr-2 shrink-0" data-testid={`stroke-folder-reorder-${category.replace(/\s+/g, '-').toLowerCase()}`}>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-11 w-11"
                          disabled={!canMoveBlockUp}
                          onClick={(e) => { e.stopPropagation(); moveStrokeBlock(category, 'up'); }}
                          aria-label={`Move ${category} block up`}
                          data-testid={`stroke-folder-move-up-${category.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <ArrowUp className="w-5 h-5" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-11 w-11"
                          disabled={!canMoveBlockDown}
                          onClick={(e) => { e.stopPropagation(); moveStrokeBlock(category, 'down'); }}
                          aria-label={`Move ${category} block down`}
                          data-testid={`stroke-folder-move-down-${category.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <ArrowDown className="w-5 h-5" />
                        </Button>
                      </div>
                      <AccordionTrigger className="px-2 py-3 text-sm font-medium hover:no-underline flex-1">
                        <span className="flex items-center gap-2">
                          {category}
                          <Badge variant="secondary" className="font-normal">
                            {members.length} {members.length === 1 ? 'event' : 'events'}
                          </Badge>
                        </span>
                      </AccordionTrigger>
                    </div>
                    <AccordionContent className="px-4 pb-4 space-y-3">
                      {members.map(({ e, flatIdx }, localIdx) => (
                        <EventCard
                          key={e.id}
                          event={e}
                          onUpdate={updateEvent}
                          onRemove={removeEvent}
                          onMoveUp={() => {
                            // Swap with the previous member of the
                            // same stroke folder (not the flat-array
                            // neighbor, which may be a different
                            // stroke). Reuses moveEvent by walking
                            // flat indices to find the prev sibling.
                            const prev = members[localIdx - 1];
                            if (!prev) return;
                            setEvents((curr) => {
                              const next = [...curr];
                              [next[flatIdx], next[prev.flatIdx]] = [next[prev.flatIdx], next[flatIdx]];
                              return next;
                            });
                          }}
                          onMoveDown={() => {
                            const nxt = members[localIdx + 1];
                            if (!nxt) return;
                            setEvents((curr) => {
                              const next = [...curr];
                              [next[flatIdx], next[nxt.flatIdx]] = [next[nxt.flatIdx], next[flatIdx]];
                              return next;
                            });
                          }}
                          canMoveUp={localIdx > 0}
                          canMoveDown={localIdx < members.length - 1}
                          genderLabels={genderLabels}
                          singleGenderMode={false}
                          useAgeDropdowns={true}
                          useDistanceDropdown={true}
                          poolUnit={data.pool_unit}
                          defaultExpanded={autoExpandedIds.has(e.id)}
                        />
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addStrokeEvent(category)}
                        className="w-full"
                        data-testid={`add-stroke-event-${category.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add {category === 'Other' ? 'Event' : `${category.replace(/s$/, '')} Event`}
                      </Button>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        ) : (
          <div className="space-y-3 mb-6">
            {events.map((event, idx) => (
              <EventCard
                key={event.id}
                event={event}
                onUpdate={updateEvent}
                onRemove={removeEvent}
                onMoveUp={() => moveEvent(idx, 'up')}
                onMoveDown={() => moveEvent(idx, 'down')}
                canMoveUp={idx > 0}
                canMoveDown={idx < events.length - 1}
                genderLabels={genderLabels}
                singleGenderMode={sportType === 'swim'}
                xcDurationRecompute={sportType === 'xc'}
              />
            ))}
          </div>
        )}

        {/* Add Event from Predefined List */}
        <Card className="p-4 mb-4">
          <h3 className="font-medium mb-3">Add {vocab.event}</h3>
          <div className="flex gap-2">
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder={`Select ${vocab.event === 'Event' ? 'an event' : 'a ' + vocab.event.toLowerCase()} to add...`} />
              </SelectTrigger>
              <SelectContent>
                {getAvailableEvents().map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.name}{event.ageGroup ? ` — ${event.ageGroup}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={addPredefinedEvent} disabled={!selectedEventId}>
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </Card>

        {/* Bulk / Custom entry. Summer-Club mode hides the global
            Bulk Event Builder entirely — the inline per-stroke
            "+ Add {Stroke} Event" buttons inside each accordion
            folder are the new primary path. Standard HS Swim still
            gets the Bulk Builder for the gender × age permutation
            workflow; track + xc keep the "Add Custom Event" card. */}
        {sportType === 'swim' && !isSummerClub ? (
          <BulkEventBuilder
            // Remount when either pool unit or preset switches so the
            // builder picks up the new chip catalog (youth-club vs
            // scholastic) and clears any stale selections from the
            // previous mode.
            key={`${data.pool_unit || 'yards'}-${data.swim_preset || 'standard_hs'}`}
            poolUnit={data.pool_unit}
            swimPreset={data.swim_preset}
            vocab={vocab}
            onAdd={(rows) => setEvents((prev) => [...prev, ...rows])}
          />
        ) : sportType !== 'swim' ? (
          <Card className="p-4 mb-6" data-testid="add-custom-event-card">
            <h3 className="font-medium mb-1">Add Custom {vocab.event}</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Quick-add for events not in the preset. Defaults to
              {' '}{CUSTOM_EVENT_DEFAULT_DURATION_SECONDS}s per heat — adjust
              inside the {vocab.event.toLowerCase()} card after adding.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input
                type="text"
                placeholder={`${vocab.event} name`}
                value={customEventName}
                onChange={(e) => setCustomEventName(e.target.value)}
                data-testid="custom-event-name-input"
                className="sm:col-span-2"
              />
              <Input
                type="text"
                placeholder="Age Group (optional)"
                value={customEventAgeGroup}
                onChange={(e) => setCustomEventAgeGroup(e.target.value)}
                data-testid="custom-event-age-group-input"
              />
            </div>
            <Button
              onClick={addCustomEvent}
              disabled={!customEventName.trim()}
              className="w-full mt-3"
              data-testid="add-custom-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Custom {vocab.event}
            </Button>
          </Card>
        ) : null}
      </main>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
        <div className="max-w-2xl mx-auto">
          <Button 
            className="w-full" 
            size="lg"
            onClick={handleContinue}
            disabled={enabledCount === 0}
          >
            {editMode ? `Save Changes (${enabledCount} ${vocab.events})` : `Create Meet (${enabledCount} ${vocab.events})`}
          </Button>
        </div>
      </div>
    </div>
  );
}
